// src/hooks/useNotifications.js
import { useState, useCallback, useRef, useEffect } from 'react';

export const useNotifications = () => {
  const [alerts, setAlerts] = useState([]);
  const alertTimeoutsRef = useRef({});
  const notificationHistoryRef = useRef(new Set());
  const lastNotificationTimeRef = useRef({});
  const debounceTimersRef = useRef({});

  // Cleanup function for all timers
  useEffect(() => {
    return () => {
      // Clear all timeouts on unmount
      Object.values(alertTimeoutsRef.current).forEach(clearTimeout);
      Object.values(debounceTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  // Generate a unique key for notification deduplication
  const getNotificationKey = useCallback((payload) => {
    return `${payload.type || 'info'}-${payload.title || ''}-${payload.message || ''}`;
  }, []);

  // Check if notification should be throttled (prevent spam)
  const shouldThrottle = useCallback((key, throttleTime = 1000) => {
    const now = Date.now();
    const lastTime = lastNotificationTimeRef.current[key] || 0;
    
    if (now - lastTime < throttleTime) {
      return true;
    }
    
    lastNotificationTimeRef.current[key] = now;
    return false;
  }, []);

  // Debounced notification to prevent rapid-fire alerts
  const showDebouncedAlert = useCallback((payload, delay = 300) => {
    const key = getNotificationKey(payload);
    
    // Clear existing debounce timer for this notification type
    if (debounceTimersRef.current[key]) {
      clearTimeout(debounceTimersRef.current[key]);
    }
    
    debounceTimersRef.current[key] = setTimeout(() => {
      showAlert(payload);
      delete debounceTimersRef.current[key];
    }, delay);
  }, [getNotificationKey]);

  const showAlert = useCallback((payload = {}) => {
    const key = getNotificationKey(payload);
    
    // Prevent duplicate notifications within a short time frame
    if (notificationHistoryRef.current.has(key) && shouldThrottle(key, 2000)) {
      return;
    }
    
    const id = `${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const alert = { 
      id, 
      title: payload.title || "Alert", 
      message: payload.message || "", 
      type: payload.type || "info", 
      ts: Date.now(),
      persistent: payload.persistent || false // Add persistent flag
    };
    
    setAlerts(prev => {
      // Check if identical alert already exists
      const exists = prev.some(a => 
        a.title === alert.title && 
        a.message === alert.message && 
        a.type === alert.type
      );
      
      if (exists) {
        return prev; // Don't add duplicate
      }
      
      return [alert, ...prev].slice(0, 8);
    });

    // Add to history
    notificationHistoryRef.current.add(key);
    
    // Clean up old history entries (keep only last 50)
    if (notificationHistoryRef.current.size > 50) {
      const entries = Array.from(notificationHistoryRef.current);
      notificationHistoryRef.current = new Set(entries.slice(-25));
    }

    // Clear any existing timeout for this ID
    if (alertTimeoutsRef.current[id]) {
      clearTimeout(alertTimeoutsRef.current[id]);
    }

    // Only set auto-dismiss timeout if not persistent
    if (!alert.persistent) {
      alertTimeoutsRef.current[id] = setTimeout(() => {
        removeAlert(id);
      }, 6000);
    }
  }, [getNotificationKey, shouldThrottle]);

  const removeAlert = useCallback((id) => {
    setAlerts(prev => prev.filter(a => a.id !== id));
    if (alertTimeoutsRef.current[id]) {
      clearTimeout(alertTimeoutsRef.current[id]);
      delete alertTimeoutsRef.current[id];
    }
  }, []);

  // Clear all alerts
  const clearAllAlerts = useCallback(() => {
    setAlerts([]);
    // Clear all timeouts
    Object.values(alertTimeoutsRef.current).forEach(clearTimeout);
    alertTimeoutsRef.current = {};
  }, []);

  // Show connection status notification with safeguards
  const showConnectionAlert = useCallback((status, error = null) => {
    const statusKey = `connection-${status}`;
    
    // Throttle connection notifications
    if (shouldThrottle(statusKey, 3000)) {
      return;
    }

    let payload = {
      type: 'info',
      title: 'Connection Status',
      message: `Status: ${status}`
    };

    switch (status) {
      case 'connecting':
        payload = {
          type: 'info',
          title: 'Connecting',
          message: 'Establishing connection...'
        };
        break;
      case 'connected':
        payload = {
          type: 'success',
          title: 'Connected',
          message: 'Connection established successfully'
        };
        break;
      case 'disconnected':
        payload = {
          type: 'warning',
          title: 'Disconnected',
          message: 'Connection lost. Attempting to reconnect...'
        };
        break;
      case 'error':
        payload = {
          type: 'error',
          title: 'Connection Error',
          message: error || 'Failed to establish connection',
          persistent: true // Keep error notifications visible
        };
        break;
      case 'reconnecting':
        // Use debounced alert for reconnection attempts
        showDebouncedAlert({
          type: 'info',
          title: 'Reconnecting',
          message: 'Attempting to reconnect...'
        });
        return;
      default:
        return;
    }

    showAlert(payload);
  }, [shouldThrottle, showAlert, showDebouncedAlert]);

  return { 
    alerts, 
    showAlert, 
    removeAlert,
    clearAllAlerts,
    showConnectionAlert,
    showDebouncedAlert
  };
};
